import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import OrderPDF from './OrderPDF'
import { useLocation } from 'react-router-dom'
import Order from './Order'
import { getObject, getObjectsWithFilter, addObject, editObjectFilter } from '../../../api/apiClientCrud'
import TransformIcon from '@mui/icons-material/Transform'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import Toolbar from '../../toolbar/Toolbar'
import { useOutletContext } from 'react-router-dom'
import SettingsIcon from '@mui/icons-material/Settings'
import EuroIcon from '@mui/icons-material/Euro'
import EmailIcon from '@mui/icons-material/Email'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import PDFReader from '../../pdfReader/PDFReader'
import {
  copyOrder,
  createAdjustmentOrder,
  createCreditNote,
  createEntity,
  createInvoiceFromOrder,
  fileToBase64,
  updateAttachments,
  updateDocumentStatus
} from '../../../utils/documentManager'
import useNavigate from '../../hooks/useNavigate'
import ActionMenu from '../../common/ActionMenu'
import StatusField from '../../fields/StatusField'
import Loading from '../../common/Loading'
import StatusDialog from '../../dialogs/StatusDialog'
import PaymentDialog from '../../dialogs/PaymentDialog'
import NotFound from '../../common/NotFound'
import DocumentPreview from '../../document/DocumentPreview'
import { pdf } from '@react-pdf/renderer'
import Income from '../Accounting/Income'
import { ContentCopy } from '@mui/icons-material'

const ViewOrder = () => {
  const [order, setOrder] = useState({})
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [statusList, setStatusList] = useState([])
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [settings, setSettings] = useState(null)
  const toastId = useRef(null)
  const history = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'commande',
        object: order,
        objectType: 'order',
        user,
        company,
        customer: order.customer,
        template: company.order_email_template,
        component: <OrderPDF order_param={order} company_param={company} logo_param={img} settings={settings} />,
        setFiles,
        subjectPrefix: 'Votre commande',
        statusToUpdate: 70,
        updateStatus: (newStatus) => {
          setOrder((prev) => ({ ...prev, status: newStatus }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [order, user, company, img, settings])

  const sendNotifDelivery = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'commande',
        object: order,
        objectType: 'order',
        user,
        company,
        customer: order.customer,
        template: company.order_shipped_email_template,
        subjectPrefix: 'Expédition de votre commande '
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [order, user, company, img, settings])

  const transform = useCallback(async () => {
    try {
      let quote = null
      if (order.quote_id) quote = await getObject('quotes', order.quote_id)

      createEntity({
        toast,
        toastIdRef: toastId,
        history,
        user,
        entityType: 'invoices',
        settingsKey: 'invoice_number',
        contractLineName: 'Invoices',
        factoryFn: createInvoiceFromOrder,
        args: [order, quote?.data],
        redirectPath: '/invoices/edit',
        successMessage: 'La facture a été créée avec succès',
        updateHistoryState: { history: '/invoices/view' },
        onSuccess: () => () => {
          setOrder((prev) => ({ ...prev, status: 100 }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de la création de la facture')
    }
  }, [order, user, history])

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'orders',
      title: () => {
        return `Commande ${order.order_number}`
      },
      accessRight: userRight,
      actions: {
        edit: true,
        editDisabled: () => order.status >= 70,
        files: true,
        back: true,
        audits: true,
        list: true
      },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: () => sendEmail(),
          disabled: () => !order.customer?.email
        },
        {
          icon: () => <EuroIcon />,
          tooltip: 'Payer',
          onClick: () => setIsPaymentDialogOpen(true),
          disabled: () => order.status >= 80 || order.status < 70
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: () => setIsStatusDialogOpen(true),
          disabled: () => order.status >= 80
        },
        {
          icon: () => <ContentCopy />,
          tooltip: 'Dupliquer',
          onClick: () => duplicate(),
          disabled: () => order.quote_id !== null || order.intervention_id !== null
        },
        {
          icon: () => <TransformIcon />,
          tooltip: 'Facturer',
          onClick: transform,
          disabled: () => order.status !== 90
        }
      ],
      customComponentsLeft: [
        {
          component: () => <StatusField value={order.status} type='orders' statusList={statusList} />
        }
      ]
    }),
    [userRight, order, statusList, sendEmail]
  )

  const duplicate = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'orders',
      settingsKey: 'order_number',
      factoryFn: copyOrder,
      args: [order],
      redirectPath: '/orders/edit',
      successMessage: 'La commande a été dupliquée avec succès',
      updateHistoryState: { history: '/orders/view' }
    })
  }, [toast, history, user, order])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== order.status) {
        setOrder((prevOrder) => ({ ...prevOrder, status: newValue }))
        setIsStatusChanged(true)
        if (newValue === 70) sendNotifDelivery()
      }
    },
    [order.status]
  )

  const closePaymentDialog = useCallback(
    (newValue) => {
      setIsPaymentDialogOpen(false)
      if (newValue?.id !== undefined && newValue.id === order.id) {
        setOrder((prev) => ({ ...prev, status: 80 }))
      }
    },
    [order.id]
  )

  useEffect(() => {
    if (!order?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = order.attachments?.find((f) => f.type === 'order')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <OrderPDF order_param={order} company_param={company} logo_param={img} settings={settings} />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [order, company, img, settings])

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('orders', order, user, toast, setIsStatusChanged)
  }, [isStatusChanged, order.id, user.email])

  useEffect(() => {
    setOrder((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    const orderId = location?.state?.id
    if (!orderId) {
      toast.error('Commande introuvable.', { autoClose: 2000 })
      return
    }
    const fetchAll = async () => {
      try {
        const [statusRes, orderRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'orders' }),
          getObject('orders', orderId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setOrder(orderRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (orderRes.data.attachments) {
          setFiles(orderRes.data.attachments)
          const attach = orderRes.data.attachments.find((f) => f.type === 'order')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!order?.id) return <NotFound title='Commande introuvable' />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', flexGrow: 1 }}>
      <Toolbar
        options={toolbarOptions}
        object={order}
        setObject={setFiles}
        setParentObject={setOrder}
        parentObject={order}
      />
      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`commande_${order?.order_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={Order} document={order} />
        </Box>
      )}

      <StatusDialog
        open={isStatusDialogOpen}
        value={order.status}
        onClose={closeStatusDialog}
        type='orders'
        statusList={statusList}
      />
      <PaymentDialog
        FormComponent={Income}
        defaultTitle='Payer commande'
        open={isPaymentDialogOpen}
        data={order}
        onClose={closePaymentDialog}
      />
    </Box>
  )
}

export default ViewOrder
